import { CompanyRole } from "@funded-here-interface/common/src/constant/enum";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface ShareholderDirector {
  role: CompanyRole;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Representative state for institutional investor onboarding
 * Stores authorized representative and director information
 */
export interface RepState {
  repFirstName: string | null;
  repLastName: string | null;
  repTitle: string | null;
  repEmail: string | null;
  userTitle: string | null;
  shareholdersDirectors: ShareholderDirector[];
}

const initialState: RepState = {
  repFirstName: null,
  repLastName: null,
  repTitle: null,
  repEmail: null,
  userTitle: null,
  shareholdersDirectors: [],
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof RepState>(field: K) => {
  return (state: RepState, action: PayloadAction<RepState[K]>) => {
    state[field] = action.payload;
  };
};

const repSlice = createSlice({
  name: "institutionalRep",
  initialState,
  reducers: {
    // Representative information setters
    setRepFirstNameRedux: createFieldSetter("repFirstName"),
    setRepLastNameRedux: createFieldSetter("repLastName"),
    setRepTitleRedux: createFieldSetter("repTitle"),
    setRepEmailRedux: createFieldSetter("repEmail"),
    setUserTitleRedux: createFieldSetter("userTitle"),

    // Shareholder/Director setters
    setShareholderDirectorRedux: createFieldSetter("shareholdersDirectors"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateInstitutionalRepState: (state, action: PayloadAction<Partial<RepState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetInstitutionalRepState: () => initialState,
  },
});

export const {
  setRepFirstNameRedux,
  setRepLastNameRedux,
  setRepTitleRedux,
  setRepEmailRedux,
  setUserTitleRedux,
  setShareholderDirectorRedux,
  hydrateInstitutionalRepState,
  resetInstitutionalRepState,
} = repSlice.actions;

export default repSlice.reducer;
